import CloseIcon from '@mui/icons-material/Close'
import EditNoteIcon from '@mui/icons-material/EditNote'
import {Box, IconButton, Typography, Collapse} from '@mui/material'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  AnnouncementType,
  useDismissibleAnnouncement
} from '../../../shared/order-editor/hooks/useDismissibleAnnouncement'

interface MixCustomizationAnnouncementProps {
  /** Test ID for testing purposes */
  testId?: string
  /** Animation duration in milliseconds */
  animationDuration?: number
}

/**
 * Announcement component for mix customization information
 * Uses localStorage to persist dismiss state per user
 */
export const MixCustomizationAnnouncement: React.FC<MixCustomizationAnnouncementProps> = ({
  testId = 'mix-customization-announcement',
  animationDuration = 300
}) => {
  const {t} = useTranslation()
  const {isDismissed, dismiss} = useDismissibleAnnouncement(AnnouncementType.MIX_CUSTOMIZATION)
  const [isVisible, setIsVisible] = useState(false)

  // Trigger enter animation on mount
  React.useEffect(() => {
    setIsVisible(true)
  }, [])

  const handleDismiss = () => {
    setIsVisible(false)
    // Delay the actual dismiss to allow animation to complete
    setTimeout(() => {
      dismiss()
    }, animationDuration)
  }

  // Don't render if user has dismissed the announcement
  if (isDismissed) {
    return null
  }

  return (
    <Collapse in={isVisible} timeout={animationDuration}>
      <Box
        sx={{
          backgroundColor: '#171F26',
          color: '#FFFFFF',
          padding: '16px',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginTop: '16px',
          marginBottom: '16px'
        }}
        data-test-id={testId}
      >
        {/* First line: Icon, Title, and Close button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          {/* Icon */}
          <EditNoteIcon
            sx={{
              color: '#FFFFFF',
              fontSize: '16px',
              flexShrink: 0
            }}
          />

          {/* Title */}
          <Typography
            sx={{
              flex: 1,
              color: (theme) => theme.palette.common.white,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: 'Inter',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '22px'
            }}
          >
            {t('orderIntake.rmc.steps.material.mixCustomizationTitle')}
          </Typography>

          {/* Close button */}
          <IconButton
            onClick={handleDismiss}
            size="small"
            disableRipple
            sx={{
              color: (theme) => theme.palette.common.white,
              boxShadow: 'none',
              width: '24px',
              height: '24px',
              borderRadius: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            data-test-id={`${testId}-close-button`}
          >
            <CloseIcon
              fontSize="small"
              sx={{
                width: '16px',
                height: '16px'
              }}
            />
          </IconButton>
        </Box>

        {/* Second line: Message */}
        <Typography
          sx={{
            color: (theme) => theme.palette.common.white,
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '14px',
            fontStyle: 'normal',
            lineHeight: '22px'
          }}
        >
          {t('orderIntake.rmc.steps.material.mixCustomizationAnnouncement')}
        </Typography>
      </Box>
    </Collapse>
  )
}
